import { useWalletSession, type WalletSessionValue } from "@tenergy/web-core/wallet";
import { useAdminSession } from "./session/use-admin-session.js";
import { useEffect } from "preact/hooks";
import { adminEnv } from "./env.js";
import { SCREEN_LABELS, useRouter, type Router } from "./router.js";
import { SignInScreen } from "./sign-in.js";
import { ConfirmDialog } from "./auth/confirm-dialog.js";
import { AdminsScreen } from "./screens/admins.js";
import { SecurityScreen } from "./screens/security.js";
import { Shell } from "./shell.js";
import { Note, Spinner } from "./ui.js";
import { WalletPicker } from "./wallet-picker.js";
import { AdminWalletContext } from "./session/wallet-context.js";
import { AccountProfileScreen } from "./screens/account-profile.js";
import { AccountsScreen } from "./screens/accounts.js";
import { AlertsScreen } from "./screens/alerts.js";
import { AuditLogScreen } from "./screens/audit-log.js";
import { ContractsScreen } from "./screens/contracts.js";
import { FinanceScreen } from "./screens/finance.js";
import { FundReportScreen } from "./screens/fund-report.js";
import { McpUsageScreen } from "./screens/mcp-usage.js";
import { NetworkScreen } from "./screens/network.js";
import { PoolsScreen } from "./screens/pools.js";
import { MonitoringScreen } from "./screens/monitoring.js";
import { ProvidersScreen } from "./screens/providers.js";
import { WalletsScreen } from "./screens/wallets.js";
import { OrderBookScreen } from "./screens/order-book.js";
import { OrderDetailScreen, OrdersScreen } from "./screens/orders.js";
import { NodeKeysScreen } from "./screens/node-keys.js";
import { WithdrawalsScreen } from "./screens/withdrawals.js";
import { PricesScreen } from "./screens/prices.js";
import { PromoCodesScreen } from "./screens/promo-codes.js";
import { NileScreen } from "./screens/nile.js";
import {
  OwnAdmissionScreen,
  OwnBandwidthScreen,
  OwnDelegationsScreen,
  OwnEconomicsScreen,
  OwnPoolScreen,
  OwnSuppliersScreen,
  OwnSupplyHeader,
  OwnSupplyOverview,
} from "./screens/own-supply.js";
import { PromoAppScreen, PromoDevScreen } from "./screens/promo-token.js";
import { SubscriptionsScreen } from "./screens/subscriptions.js";
import { SupplyScreen } from "./screens/supply.js";
import { roleAtLeast, screenRoleOf, type AdminIdentity, type AdminRole } from "./api/types.js";

/**
 * The admin SPA.
 *
 * THREE STATES (docs/product/admin-auth.md):
 *   restoring   — one line of text, not a skeleton of a panel nobody may be entitled to see;
 *   signed out  — the admin sign-in and nothing else. An address without admin access never gets past it: the API
 *                 opens no session for it, so there is no "no access" panel to render;
 *   signed in   — the shell, with the screen right (viewer or editor) threaded into every screen that has a mutation.
 *
 * `role` IS PASSED DOWN, NOT READ FROM A CONTEXT, so every screen with a mutation control has
 * to name it in its signature: a screen that forgets the prop does not compile.
 */
export function AdminApp({ wallet: injected }: { wallet?: WalletSessionValue } = {}) {
  // `injected` is a TEST SEAM and nothing else: the wallet adapters live behind a dynamic
  // import and a jsdom browser has no extension, so a test that asserts the order of
  // connect → challenge → sign hands in a stub. Production always takes the branch below.
  const own = useWalletSession();
  const wallet = injected ?? own;
  const session = useAdminSession(wallet);
  const router = useRouter();
  // The tab says where you are and which network (owner 2026-10-12: "Platform admin" told nothing).
  const screenLabel = SCREEN_LABELS[router.route.screen];
  useEffect(() => {
    const net = adminEnv().tronNetwork === "nile" ? " (Nile)" : "";
    document.title = `${screenLabel ? `${screenLabel} · ` : ""}TEnergy Admin${net}`;
  }, [screenLabel]);

  if (!session.ready) return <Spinner label="Checking this session…" />;

  if (session.identity === null) {
    return (
      <>
        <SignInScreen session={session} />
        <WalletPicker wallet={wallet} />
      </>
    );
  }

  return (
    <AdminWalletContext.Provider value={wallet}>
      <Shell
        address={session.identity.address}
        role={session.identity.role}
        router={router}
        onSignOut={() => {
          void session.signOut().then(() => router.navigate("monitoring"));
        }}
      >
        <Screen router={router} identity={session.identity} wallet={wallet} />
      </Shell>
      <ConfirmDialog wallet={wallet} />
      <WalletPicker wallet={wallet} />
    </AdminWalletContext.Provider>
  );
}

function Screen({ router, identity, wallet }: { router: Router; identity: AdminIdentity; wallet: WalletSessionValue }) {
  const { screen, param } = router.route;
  const role: AdminRole = screenRoleOf(identity.role);
  switch (screen) {
    case "security":
      return <SecurityScreen identity={identity} wallet={wallet} />;
    case "admins":
      return roleAtLeast(identity.role, "admin") ? (
        <AdminsScreen identity={identity} />
      ) : (
        <Note tone="warning">Admins is for the admin and root roles.</Note>
      );
    case "supply":
      return <SupplyScreen role={role} />;
    case "order-book":
      return <OrderBookScreen />;
    case "prices":
      return <PricesScreen role={role} />;
    case "promo-codes":
      return <PromoCodesScreen role={role} />;
    case "promo-app":
      return <PromoAppScreen role={role} />;
    case "promo-dev":
      return <PromoDevScreen />;
    case "nile":
      return <NileScreen router={router} />;
    case "contracts":
      return <ContractsScreen role={role} />;
    case "orders":
      return param === null ? (
        <OrdersScreen router={router} />
      ) : (
        <OrderDetailScreen id={param} router={router} />
      );
    case "subscriptions":
      return <SubscriptionsScreen />;
    case "node-keys":
      return <NodeKeysScreen />;
    case "accounts":
      return param === null ? (
        <AccountsScreen role={role} router={router} />
      ) : (
        <AccountProfileScreen id={param} role={role} router={router} />
      );
    case "finance":
      return <FinanceScreen router={router} />;
    case "fund-report":
      return <FundReportScreen />;
    case "mcp-usage":
      return <McpUsageScreen />;
    case "network":
      return <NetworkScreen router={router} />;
    case "pools":
      return <PoolsScreen router={router} provider={param} />;
    case "alerts":
      return <AlertsScreen role={role} />;
    case "audit":
      return <AuditLogScreen />;
    case "withdrawals":
      return <WithdrawalsScreen role={role} />;
    case "monitoring":
      return <MonitoringScreen />;
    case "wallets":
      return <WalletsScreen />;
    case "providers":
      return <ProvidersScreen role={role} />;
    case "own-supply":
      return (
        <>
          <OwnSupplyHeader view="overview" />
          {param === null ? (
            <OwnSupplyOverview onPool={(address) => router.navigate("own-supply", address)} />
          ) : (
            <OwnPoolScreen address={param} role={role} onBack={() => router.navigate("own-supply")} />
          )}
        </>
      );
    case "own-delegations":
      return (
        <>
          <OwnSupplyHeader view="delegations" />
          <OwnDelegationsScreen onOrder={(id) => router.navigate("orders", id)} />
        </>
      );
    case "own-admission":
      return (
        <>
          <OwnSupplyHeader view="admission" />
          <OwnAdmissionScreen />
        </>
      );
    case "own-economics":
      return (
        <>
          <OwnSupplyHeader view="economics" />
          <OwnEconomicsScreen />
        </>
      );
    case "own-bandwidth":
      return (
        <>
          <OwnSupplyHeader view="bandwidth" />
          <OwnBandwidthScreen />
        </>
      );
    case "own-suppliers":
      return (
        <>
          <OwnSupplyHeader view="suppliers" />
          <OwnSuppliersScreen />
        </>
      );
    default:
      // Unreachable: `parseRoute` maps anything unknown to the default screen. Kept so that
      // adding a screen to the manifest without adding it here is a type error.
      return <Note tone="warning">No such screen.</Note>;
  }
}
